import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ValidationLogin.module.scss';
import { notify } from '../../services/toastService';

interface ValidationLoginProps {
    email?: string;
}

export default function ValidationLogin({ email = 'usuario@exemplo.com' }: ValidationLoginProps) {
    const [code, setCode] = useState<string[]>(['', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [timeLeft, setTimeLeft] = useState(300); // 5 minutos em segundos
    const [canResend, setCanResend] = useState(false);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();

    // Timer para reenvio do código
    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [timeLeft]);

    // Formatar tempo restante
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Manipular entrada do código
    const handleInputChange = (index: number, value: string) => {
        if (value.length > 1) return; // Apenas um dígito
        if (!/^\d*$/.test(value)) return; // Apenas números

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');

        // Mover para próximo input
        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }

        // Verificar se código está completo
        if (newCode.every((digit) => digit !== '') && value) {
            handleSubmit(newCode.join(''));
        }
    };

    // Manipular teclas especiais
    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Colar código
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

        if (pastedData.length === 4) {
            const newCode = pastedData.split('');
            setCode(newCode);
            setError('');
            handleSubmit(pastedData);
        }
    };

    // Submeter código para validação
    const handleSubmit = (codeToValidate: string) => {
        setIsLoading(true);
        setError('');

        try {
            console.log('Código enviado:', codeToValidate);
            notify.success('Código enviado com sucesso!');
        } catch (err) {
            setError('Erro ao validar código. Tente novamente.');
            console.log(err);
            notify.error('Código inválido. Tente novamente.');
            setCode(['', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setIsLoading(false);
        }
    };

    // // Reenviar código
    // const handleResendCode = async () => {
    //     if (!canResend) return;

    //     setIsLoading(true);
    //     try {
    //         // Simular reenvio
    //         await new Promise((resolve) => setTimeout(resolve, 1000));

    //         onResendCode?.();
    //         setTimeLeft(300);
    //         setCanResend(false);
    //         setError('');
    //         setCode(['', '', '', '']);

    //         // Feedback visual
    //         alert('Código reenviado com sucesso!');
    //     } catch (err) {
    //         setError('Erro ao reenviar código. Tente novamente.');
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    return (
        <div className={styles.validationContainer}>
            <div className={styles.validationCard}>
                <h2>Validação de Código</h2>

                <p className={styles.instruction}>
                    Enviamos um código de 4 dígitos para:
                    <strong>{email}</strong>
                </p>

                <div className={styles.codeInputContainer}>
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            className={`${styles.codeInput} ${error ? styles.error : ''}`}
                            disabled={isLoading}
                            autoFocus={index === 0}
                        />
                    ))}
                </div>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <div className={styles.timerContainer}>
                    {!canResend ? (
                        <p className={styles.timer}>
                            Reenviar código em: <span>{formatTime(timeLeft)}</span>
                        </p>
                    ) : (
                        <button
                            onClick={() => handleSubmit(code.join(''))}
                            disabled={isLoading}
                            className={styles.resendButton}
                        >
                            {isLoading ? 'Reenviando...' : 'Reenviar código'}
                        </button>
                    )}
                </div>

                <div className={styles.actions}>
                    <button
                        onClick={() => void navigate('/login')}
                        className={styles.backButton}
                        disabled={isLoading}
                    >
                        Voltar ao Login
                    </button>
                </div>

                {isLoading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}></div>
                    </div>
                )}
            </div>
        </div>
    );
}
